/**
 * `Perfis de coleta · Novo perfil` (`309:10810`) and a card's "Duplicar": with a source picked,
 * this posts a copy of that profile under the name typed here; with none, it posts to
 * `monitoring-profiles/blank`, whose body is just the name. Either way `Criar e configurar`
 * finishes by opening the new profile in `ProfileEditor`.
 *
 * "Duplicar" opens it already open, with the card's profile as the source and a copy's name
 * filled in (`initialSourceId`, `initialName`).
 */
import { useEffect, useId, useState, type FormEvent, type ReactNode } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';

import { FormDialog, fieldControlProps, fieldErrorOf } from '../../../../chrome/form';
import type { ApiFailure, ApiFailureError } from '../../../../lib/http/errors';
import { Field, FieldError, FieldLabel } from '../../../../ui/field';
import { Input } from '../../../../ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../../ui/select';
import { useApiClient } from '../../../session/client';
import { PROFILE_EDITOR_PATH } from '../../paths';
import {
  ct,
  duplicateProfileBody,
  monitoringProfileOutSchema,
  PROFILES_BLANK_PATH,
  PROFILES_PATH,
  PROFILES_QUERY_KEY,
  type MonitoringProfileOut,
  type MonitoringProfilePriced,
} from '../Catalogue';

/** A real, always-present picker option, not an absence of one --
 *  create-from-blank stays reachable once the tenant has a profile to copy
 *  from, not only when it has none. Not the empty string: Radix's `Select.
 *  Item` refuses one, since it reserves `''` to mean "nothing selected". */
const BLANK_SOURCE = 'blank';

/** The slug is derived from the name, so the unique-slug 409 means this
 *  organisation already has a profile by that name -- said on the name field,
 *  in the reader's words rather than the server's. */
function displayFailure(error: ApiFailureError | null): ApiFailure | null {
  if (error === null) return null;
  if (error.failure.kind === 'conflict') {
    return { kind: 'invalid', field: 'name', message: ct('newProfile.nameTaken') };
  }
  return error.failure;
}

function slugify(name: string): string {
  return (
    name
      .normalize('NFD')
      .replace(/\p{Diacritic}/gu, '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '') || 'perfil'
  );
}

export function NewProfileDialog(props: {
  tenantId: string;
  profiles: MonitoringProfilePriced[];
  trigger?: ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  title?: string;
  initialSourceId?: string;
  initialName?: string;
}): ReactNode {
  const { tenantId, profiles, trigger } = props;
  const client = useApiClient();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [ownOpen, setOwnOpen] = useState(false);
  const open = props.open ?? ownOpen;
  const setOpen = (next: boolean): void => {
    setOwnOpen(next);
    props.onOpenChange?.(next);
  };
  const [name, setName] = useState(props.initialName ?? '');
  const [sourceId, setSourceId] = useState<string>(
    props.initialSourceId ?? profiles[0]?.id ?? BLANK_SOURCE,
  );
  const [sourceTouched, setSourceTouched] = useState(props.initialSourceId !== undefined);
  const nameId = useId();
  const nameErrorId = useId();
  const sourceFieldId = useId();

  // `profiles` is still `[]` while the listing is pending, so the state
  // above seeds blank on the very first render; once real profiles arrive,
  // default to the first one -- but only until the tenant has actually
  // chosen a source themselves.
  useEffect(() => {
    if (!sourceTouched && sourceId === BLANK_SOURCE && profiles.length > 0) {
      setSourceId(profiles[0]!.id);
    }
  }, [profiles, sourceTouched, sourceId]);

  const mutation = useMutation<MonitoringProfileOut, ApiFailureError, void>({
    mutationFn: () => {
      const source = profiles.find((profile) => profile.id === sourceId);
      if (sourceId !== BLANK_SOURCE && source !== undefined) {
        const body = { ...(duplicateProfileBody(source) as object), name, slug: slugify(name) };
        return client.postParsed(PROFILES_PATH(tenantId), body, monitoringProfileOutSchema);
      }
      return client.postParsed(
        PROFILES_BLANK_PATH(tenantId),
        { slug: slugify(name), name, description: null },
        monitoringProfileOutSchema,
      );
    },
    onSuccess: async (created) => {
      setOpen(false);
      setName('');
      setSourceTouched(false);
      await navigate({ to: PROFILE_EDITOR_PATH, params: { tenantId, profileId: created.id } });
    },
    // A 201 whose body then fails to parse still created the row.
    onSettled: () => queryClient.invalidateQueries({ queryKey: PROFILES_QUERY_KEY(tenantId) }),
  });
  const failure = displayFailure(mutation.error);

  function submit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    mutation.mutate();
  }

  return (
    <FormDialog
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) mutation.reset();
      }}
      trigger={trigger}
      title={props.title ?? ct('newProfile.title')}
      submitLabel={ct('newProfile.submit')}
      pending={mutation.isPending}
      submitDisabled={name.trim() === ''}
      failure={failure}
      onSubmit={submit}
    >
      <Field data-invalid={fieldErrorOf(failure, 'name') !== null || undefined}>
        <FieldLabel htmlFor={nameId}>{ct('newProfile.name')}</FieldLabel>
        <Input
          id={nameId}
          value={name}
          onChange={(event) => setName(event.target.value)}
          {...fieldControlProps(failure, 'name', nameErrorId)}
        />
        <FieldError id={nameErrorId}>{fieldErrorOf(failure, 'name')}</FieldError>
      </Field>
      <Field>
        <FieldLabel htmlFor={sourceFieldId}>{ct('newProfile.source')}</FieldLabel>
        <Select
          value={sourceId}
          onValueChange={(value) => {
            setSourceTouched(true);
            setSourceId(value);
          }}
        >
          <SelectTrigger id={sourceFieldId} aria-label={ct('newProfile.source')}>
            <SelectValue placeholder={ct('newProfile.source')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={BLANK_SOURCE}>{ct('newProfile.sourceBlank')}</SelectItem>
            {profiles.map((profile) => (
              <SelectItem key={profile.id} value={profile.id}>
                {ct('newProfile.sourcePrefix', { name: profile.name })}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </Field>
    </FormDialog>
  );
}
